import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { useCurrentStateAndParams } from "@uirouter/react";
import { useDispatch } from "react-redux";

import { useTranslate } from "@similarweb/i18n-provider";
import { colorsPalettes } from "@similarweb/styles";
import { Box, Flex, Text } from "@similarweb/ui-components/foundations";

import { showSuccessToast } from "app/actions/toast_actions";
import FeedbackButton from "app/pages/sales-intelligence/modules/ai-agent/components/FeedbackButton/FeedbackButton";
import { LoadingView } from "app/pages/sales-intelligence/modules/ai-agent/components/LoadingView/LoadingView";
import { useTrackCopyText } from "app/pages/sales-intelligence/modules/ai-agent/hooks/useTrackCopyText";
import { aiAgentQueryKeys } from "app/pages/sales-intelligence/modules/ai-agent/queries/queryKeyFactory";
import useCreateEmailThreadQuery from "app/pages/sales-intelligence/modules/ai-agent/queries/useCreateEmailThreadQuery";
import useFeedbackMutation from "app/pages/sales-intelligence/modules/ai-agent/queries/useFeedbackMutation";
import { useGetEmailThreadById } from "app/pages/sales-intelligence/modules/ai-agent/queries/useGetEmailThreadById";
import ParseJSONService from "app/pages/sales-intelligence/modules/ai-agent/services/jsonParser/jsonParserService";
import type { ParsedAIEmailPreview } from "app/pages/sales-intelligence/modules/ai-agent/services/jsonParser/types";
import AIAgentTrackingService from "app/pages/sales-intelligence/modules/ai-agent/services/tracking/trackingService";
import salesSettingsHelper from "app/pages/sales-intelligence/services/salesSettingsHelper";
import { EmailWidgetBody } from "app/pages/sales-intelligence/modules/ai-agent/sub-modules/super-agent/components/EmailWidget/EmailWidgetBody";
import { EmailWidgetWrapper } from "app/pages/sales-intelligence/modules/ai-agent/sub-modules/super-agent/components/EmailWidget/EmailWidgetWrapper";
import { StyledCopyButton } from "app/pages/sales-intelligence/modules/ai-agent/sub-modules/super-agent/components/EmailWidget/styles";
import {
    addThreadIdAndGeneratedEmailToMessage,
    copyEmailToClipboard,
    parseEmailFromHistoryMessage,
} from "app/pages/sales-intelligence/modules/ai-agent/sub-modules/super-agent/components/EmailWidget/utils";
import { useUpdateMessage } from "app/pages/sales-intelligence/modules/ai-agent/sub-modules/super-agent/queries/useUpdateMessage";
import type { AiAgentEmailPayload } from "app/pages/sales-intelligence/modules/ai-agent/types/email";
import type { EmailWidgetData } from "app/pages/sales-intelligence/modules/ai-agent/types/superAgent";
import type { FeedbackReaction } from "app/pages/sales-intelligence/modules/ai-agent/types/thread";

type Props = {
    widgetData: EmailWidgetData;
    getMessageContent: () => string;
    setMessageContent: (content: string) => void;
    messageId: string;
    isStreaming: boolean;
};

export const EmailWidget = ({
    widgetData,
    getMessageContent,
    setMessageContent,
    messageId: superAgentMessageId,
    isStreaming,
}: Props) => {
    const dispatch = useDispatch();
    const { translate } = useTranslate();
    const {
        params: { id: superAgentThreadId },
    } = useCurrentStateAndParams<{ id: string }>();

    const { mutate } = useFeedbackMutation({ isEmail: true });
    const [reaction, setReaction] = useState<FeedbackReaction | undefined>(undefined);

    const graphRef = useRef<HTMLDivElement>(null!);
    const [copyTrackElement, setCopyTrackElement] = useState<HTMLDivElement | null>(null);
    const [isCopying, setIsCopying] = useState(false);

    const setWrapperRef = useCallback((el: HTMLDivElement | null) => {
        setCopyTrackElement(el);
    }, []);

    useTrackCopyText(AIAgentTrackingService.superAgent.trackEmailWidgetCopy, copyTrackElement);

    const { domain, emailContext, threadId: widgetThreadId, generatedEmail } = widgetData;

    const hasValidGeneratedEmail =
        !!generatedEmail && typeof generatedEmail.subject === "string" && typeof generatedEmail.body === "string";

    const shouldFetchThread = !!widgetThreadId && !hasValidGeneratedEmail;
    const { data: emailThread, isLoading: isLoadingEmailThread } = useGetEmailThreadById({
        threadId: shouldFetchThread ? widgetThreadId : undefined,
    });

    const { mutate: updateMessageMutation } = useUpdateMessage();

    const payload: AiAgentEmailPayload | null =
        widgetThreadId || hasValidGeneratedEmail || isStreaming
            ? null
            : {
                  domain,
                  companyName: domain,
                  emailContext,
              };

    const { isLoading, email, threadId, messageId } = useCreateEmailThreadQuery(
        payload,
        aiAgentQueryKeys.generateEmailThreadForSuperAgent(
            domain,
            emailContext.attendees,
            emailContext.isCompanyLevel,
            superAgentMessageId,
        ),
        (emailThreadId: string, emailPayload: string) => {
            if (!emailThreadId) {
                return;
            }
            const parsedEmail = ParseJSONService.parseEmail(emailPayload);
            const augmented = addThreadIdAndGeneratedEmailToMessage(getMessageContent(), emailThreadId, parsedEmail);
            setMessageContent(augmented);
            if (salesSettingsHelper.isSuperAgentEnabled) {
                updateMessageMutation({
                    threadId: superAgentThreadId,
                    messageId: superAgentMessageId,
                    message: augmented,
                });
            }
        },
    );

    const inlineEmail = useMemo((): ParsedAIEmailPreview | null => {
        if (!hasValidGeneratedEmail) {
            return null;
        }
        const { cleaned, json, index } = ParseJSONService.extractEmbeddedJSON(generatedEmail.body);
        const preview: ParsedAIEmailPreview = {
            subject: generatedEmail.subject,
            body: cleaned,
            chartJson: null,
            chartAnchorIndex: -1,
        };
        if (json && typeof json === "object" && json !== null) {
            preview.chartJson = json as Record<string, unknown>;
            preview.chartAnchorIndex = index;
        }
        return preview;
    }, [hasValidGeneratedEmail, generatedEmail]);

    const historyEmail = useMemo((): ParsedAIEmailPreview | null => {
        if (!emailThread?.messages?.length) {
            return null;
        }
        return parseEmailFromHistoryMessage(emailThread.messages[0]);
    }, [emailThread]);

    useEffect(() => {
        const historyMessage = emailThread?.messages?.[0];

        if (historyMessage && historyMessage.isLiked !== undefined) {
            setReaction(historyMessage.isLiked ? "like" : "dislike");
        }
    }, [emailThread?.messages]);

    const displayEmail = inlineEmail ?? historyEmail ?? email;
    const feedbackThreadId = widgetThreadId ?? threadId;
    const feedbackMessageId = emailThread?.messages?.[0]?.id ?? messageId;

    const handleSubmitFeedback = (newReaction: FeedbackReaction) => {
        if (reaction === newReaction) {
            return;
        }
        setReaction(newReaction);
        mutate({
            threadId: feedbackThreadId,
            messageId: feedbackMessageId,
            liked: newReaction === "like",
        });
        AIAgentTrackingService.superAgent.trackEmailWidgetLikeDislikeClick(newReaction === "like" ? "Like" : "Dislike");
    };

    const handleCopy = useCallback(async () => {
        if (isCopying) {
            return;
        }

        AIAgentTrackingService.superAgent.trackEmailWidgetCopyButtonClick();
        setIsCopying(true);
        try {
            await copyEmailToClipboard(displayEmail, graphRef.current, () => {
                dispatch(showSuccessToast(translate("si.super-agent.email.copied_to_clipboard")));
            });
        } finally {
            setIsCopying(false);
        }
    }, [isCopying, displayEmail, dispatch, translate]);

    if (isLoading || (shouldFetchThread && isLoadingEmailThread)) {
        return (
            <EmailWidgetWrapper isLoading>
                <Flex
                    justifyContent="center"
                    alignItems="center"
                    height="100%"
                >
                    <LoadingView showText />
                </Flex>
            </EmailWidgetWrapper>
        );
    }

    return (
        <EmailWidgetWrapper ref={setWrapperRef}>
            <Box
                padding="18px 16px"
                borderBottom={`1px solid ${colorsPalettes.carbon[50]}`}
            >
                <Text
                    fontVariant="body2"
                    color={colorsPalettes.carbon[400]}
                >
                    {displayEmail?.subject}
                </Text>
            </Box>
            <Box
                padding="12px 16px"
                borderBottom={`1px solid ${colorsPalettes.carbon[50]}`}
            >
                <EmailWidgetBody
                    email={displayEmail?.body ?? ""}
                    chartAnchorIndex={displayEmail?.chartAnchorIndex ?? 0}
                    chartJson={displayEmail?.chartJson ?? null}
                    domain={domain}
                    graphRef={graphRef}
                />
            </Box>

            <Flex
                justifyContent="space-between"
                alignItems="center"
                width="100%"
                padding="12px 16px"
            >
                <Flex>
                    <FeedbackButton
                        key="like"
                        reaction="like"
                        isActive={reaction === "like"}
                        onFeedback={() => handleSubmitFeedback("like")}
                        dataAutomation="super-agent-email-widget-like"
                    />

                    <FeedbackButton
                        key="dislike"
                        reaction="dislike"
                        isActive={reaction === "dislike"}
                        onFeedback={() => handleSubmitFeedback("dislike")}
                        dataAutomation="super-agent-email-widget-dislike"
                    />
                </Flex>
                <StyledCopyButton
                    iconName="copy-rounded"
                    onClick={() => void handleCopy()}
                    isLoading={isCopying}
                    data-automation="super-agent-email-widget-copy"
                >
                    {translate("si.pages.ai_agent.copy")}
                </StyledCopyButton>
            </Flex>
        </EmailWidgetWrapper>
    );
};
